import { useEffect, useState } from "react";
import { Loader2, Wrench, Sparkles } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";

export default function ToolsPage() {
  const [desc, setDesc] = useState("");
  const [type, setType] = useState("calculator");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  const fetchHistory = () => api.get("/tools").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { fetchHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/tools/generate", { description: desc, tool_type: type });
      setResult(data);
      fetchHistory();
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const r = result?.result;

  return (
    <DashboardLayout
      eyebrow="Free Tools Generator"
      title="Spin up SEO-optimized tool pages."
      subtitle="Generate calculators, checkers and lead-capture pages that rank and convert."
    >
      <div className="grid lg:grid-cols-[400px_1fr] gap-6">
        <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-4 h-fit lg:sticky lg:top-24" data-testid="tools-form">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Tool description</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} required rows={4}
                      placeholder="e.g. ROI calculator for SaaS founders comparing tools"
                      className="bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="tools-desc" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Tool type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="tools-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                <SelectItem value="calculator">Calculator</SelectItem>
                <SelectItem value="checker">Checker / Analyzer</SelectItem>
                <SelectItem value="generator">Generator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="tools-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Generate</>}
          </Button>
          {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="tools-error">{error}</div>}
        </form>

        <div className="space-y-4">
          {loading && <div className="h-64 bg-zinc-950 border border-zinc-800 rounded-sm animate-pulse" />}
          {r && (
            <div className="space-y-4 fade-in" data-testid="tools-result">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-[#0055FF]/15 text-[#3377FF] border-[#0055FF]/30 rounded-sm">{type}</Badge>
                  <code className="text-sm text-zinc-500">/{r.slug}</code>
                </div>
                <h2 className="font-display text-3xl font-medium tracking-tight" data-testid="tool-headline">{r.headline}</h2>
                <p className="text-zinc-400 mt-2">{r.subheadline}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="tool-inputs">
                  <span className="label-eyebrow">Input fields</span>
                  <div className="mt-3 space-y-3">
                    {(r.input_fields || []).map((f, i) => (
                      <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-3">
                        <div className="text-sm text-zinc-200">{f.label}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">name="{f.name}" · type="{f.type}"</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="tool-output">
                  <span className="label-eyebrow">Output</span>
                  <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{r.output_description}</p>
                  <span className="label-eyebrow mt-6 block">Lead capture</span>
                  <div className="mt-3 space-y-2">
                    {(r.lead_form_fields || []).map((f, i) => (
                      <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-2 text-xs">
                        <span className="text-zinc-200">{f.label}</span>
                        <span className="text-zinc-500"> · {f.type}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4">
                    <span className="label-eyebrow">CTA</span>
                    <div className="mt-1 text-[#3377FF]">{r.cta_text}</div>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="tool-seo">
                <span className="label-eyebrow">SEO</span>
                <div className="mt-3 grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-zinc-500">Meta title</div>
                    <div className="text-zinc-200">{r.seo?.meta_title}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Primary keyword</div>
                    <div className="text-zinc-200">{r.seo?.primary_keyword}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-xs text-zinc-500">Meta description</div>
                    <div className="text-zinc-200">{r.seo?.meta_description}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-xs text-zinc-500 mb-2">Secondary keywords</div>
                    <div className="flex gap-2 flex-wrap">
                      {(r.seo?.secondary_keywords || []).map((k) => (
                        <Badge key={k} variant="outline" className="rounded-sm border-zinc-700 text-zinc-400">{k}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !r && history.length > 0 && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="tools-history">
              <div className="px-6 py-4 border-b border-zinc-800">
                <span className="label-eyebrow">Recent tools</span>
              </div>
              <ul className="divide-y divide-zinc-900">
                {history.map((h) => (
                  <li key={h.id} className="px-6 py-4 hover:bg-zinc-900/50 cursor-pointer transition-colors"
                      onClick={() => setResult(h)} data-testid={`tool-history-${h.id}`}>
                    <div className="text-sm text-zinc-200 truncate">{h.result?.name || h.description}</div>
                    <div className="text-xs text-zinc-500 mt-1">{new Date(h.created_at).toLocaleString()} · {h.tool_type}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!loading && !r && history.length === 0 && (
            <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center text-zinc-500">
              <Wrench className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <p>Describe a tool to spin up its full SEO-ready page spec.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
